import { ChangeDetectorRef, Component, forwardRef, Inject, NgModuleRef, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { DestroySubscribers, ISubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { IDataService, IParsedDataResponse } from '../../../services/data/data-service.interface';
import { HintTemplateConstant } from '../../help-information/hint-dynamic/hint-dynamic.constants';
import { InfoModal } from '../../../modals/info/info.modal';
import { MODAL_TOOLTIP, MODAL_TOOLTIP_KEY_ENUM } from '../controls-with-hint/hints';
import { IMODALS_SERVICE, IModalsService } from '@platform500services/p500-ui-kit';

@Component({
  templateUrl: './baseline-cumulative.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BaselineCumulativeComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BaselineCumulativeComponent),
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class BaselineCumulativeComponent implements OnInit, ControlValueAccessor, Validator {
  constructor(
    @Inject('IDataService') private dataService: IDataService,
    @Inject(IMODALS_SERVICE) private modalService: IModalsService,
    private cd: ChangeDetectorRef,
    private moduleRef: NgModuleRef<any>
  ) {
    this.globalData = this.dataService.getData();
  }

  public globalData: IParsedDataResponse;
  public form = new UntypedFormArray([]);
  public subscribers: ISubscribers = {};
  public isDisabled = false;
  public onChange: (value: any) => void = () => {};
  public onTouch = () => {};

  public ngOnInit(): void {
    this.subscribers.formChanges = this.form.valueChanges.pipe(debounceTime(200)).subscribe((items: ICumulativeFileds[]) => {
      this.onChange(items);
      this.onTouch();
    });
  }

  public addForm(): void {
    const group = new UntypedFormGroup({
      goal_type: new UntypedFormControl(null, Validators.required),
      baseline_field: new UntypedFormControl(null, Validators.required),
    });
    this.form.push(group);
  }

  public removeForm(i: number): void {
    this.form.removeAt(i);
  }

  public validate(): ValidationErrors | null {
    return this.form.invalid ? { errors: true } : null;
  }

  public writeValue(items: ICumulativeFileds[]): void {
    this.form.clear();
    items?.forEach(() => this.addForm());
    this.form.patchValue(items);
    this.cd.detectChanges();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public openHintModal(): void {
    this.modalService.open(
      InfoModal,
      {
        data: {
          title: 'Add fields',
          subTitle: 'Info',
          body: MODAL_TOOLTIP[MODAL_TOOLTIP_KEY_ENUM.CREATE_QUALIFICATION_ADD_FIELDS],
          footerType: null,
          hasCopy: false,
          class: '-info',
        },
      },
      this.moduleRef
    );
  }

  protected readonly HintTemplateConstant = HintTemplateConstant;
}

interface ICumulativeFileds {
  goal_type: number;
  baseline_field: string;
}
